import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Button, Layer } from 'grommet';
import { Close, StatusWarning } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError, FacilityService } from '/src/api';
import { EntityIcon, FormCard, InfoBox, Text } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { Facility, ModelName } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { tooltipMessages } from '/src/lib/tooltips';
import { getModelLabel } from '/src/utils';

export type DeleteFacilityModalProps = {
  facility: Facility;
  setIsVisible: (isVisible: boolean) => void;
  setShouldRefresh: (shouldRefresh: boolean) => void;
};

export const DeleteFacilityModal: React.FC<DeleteFacilityModalProps> = observer((props) => {
  const { facility, setIsVisible } = props;

  /** Stores **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const navigate = useNavigate();
  const tooltips = tooltipMessages();

  /** State **/
  const [isDeleting, setIsDeleting] = useState(false);

  /** Computed **/
  const isLoading = !facility;
  const canDelete = userStore.isFuseAdmin;

  /** Methods **/
  const deleteFacility = async () => {
    if (isDeleting) return;
    try {
      setIsDeleting(true);
      await FacilityService.delete({ facility_ids: [facility.id] });
      setIsVisible(false);
      toast.success(toastMessages.deleteFacility.success);
      navigate(`/clients/${facility.client_id}/facilities`);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.deleteFacility.error);
    } finally {
      setIsDeleting(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    if (!canDelete) setIsVisible(false);
  }, [canDelete]);

  /** Render **/
  return (
    <Layer
      position="center"
      onEsc={() => setIsVisible(false)}
      onClickOutside={() => setIsVisible(false)}
      responsive={false}
      style={{
        width: 'auto',
        maxWidth: 'min(95vw, 500px)',
        maxHeight: '95vh',
        overflow: 'auto',
        borderRadius: '8px',
      }}
    >
      <FormCard
        width="100%"
        title={`Delete ${getModelLabel(facility, ModelName.Facility)}`}
        icon={<EntityIcon entityName={ModelName.Facility} />}
        onSubmit={() => deleteFacility()}
        isLoading={isLoading || isDeleting}
        hideRequiredText
        saveButtonLabel="DELETE"
        saveButtonLoadingLabel="DELETING..."
        headerChildren={[
          <Button
            key="close"
            pad="none"
            onClick={() => setIsVisible(false)}
            icon={<Close size="20px" />}
            tip={tooltips.close}
          />,
        ]}
      >
        <Box pad={{ horizontal: 'medium', bottom: 'medium' }}>
          <InfoBox gap="1rem" direction="row" align="center">
            <StatusWarning size="medium" color="status-warning" />
            <Text>Are you sure you want to delete this Facility?</Text>
          </InfoBox>
        </Box>
      </FormCard>
    </Layer>
  );
});
